import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Minus, Plus, Truck, RotateCcw, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Spin } from 'antd';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { BASE_URL } from '../../apis/constant';
import { useAppStore, useCheckoutStore } from '../../store/Store';
import pushToDataLayer from '../../apis/GTM';
import { getTrackingMetadata, getManualTrackingMetadata } from '../../apis/TrackingMetaData';
import CartIcon from "../../icons/CartIcon";

const ProductDrawer = () => {
  const { productDrawer, closeProductDrawer } = useAppStore();
  const theme = useTheme();
  // Check if we are on mobile to prevent rendering the "hidden" drawer's content
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <Drawer
        anchor="bottom"
        open={productDrawer.open}
        onClose={closeProductDrawer}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            height: 'auto',
            maxHeight: '80vh',
            minHeight: '70dvh',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
          }
        }}
      >
        {/* Only mount the logic if the drawer is open AND we are on mobile */}
        {productDrawer.open && isMobile && <ProductSheetContent />}
      </Drawer>

      <Drawer
        anchor="right"
        open={productDrawer.open}
        onClose={closeProductDrawer}
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            width: { sm: 400, md: 500 },
            boxSizing: 'border-box'
          }
        }}
      >
        {/* Only mount the logic if the drawer is open AND we are on desktop */}
        {productDrawer.open && !isMobile && <ProductSheetContent />}
      </Drawer>
    </>
  );
};

export default ProductDrawer;

const ProductGallery = ({ images }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const mobileScrollRef = useRef(null);
  const desktopScrollRef = useRef(null);

  const handleScroll = (direction) => {
    const scrollAmount = 80;
    if (direction === 'up' || direction === 'down') {
      if (mobileScrollRef.current) {
        const container = mobileScrollRef.current;
        const currentScroll = container.scrollTop;
        container.scrollTo({
          top: direction === 'up' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
          behavior: 'smooth'
        });
      }
    } else {
      if (desktopScrollRef.current) {
        const container = desktopScrollRef.current;
        const currentScroll = container.scrollLeft;
        container.scrollTo({
          left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  if (!images || images.length === 0) return null;

  return (
    <div className="flex justify-center w-full">
      <div className="flex gap-3 h-[200px] sm:hidden">
        <div className="w-[200px] h-[200px] bg-gray-50 rounded-xl overflow-hidden border border-gray-100 relative shadow-sm flex-shrink-0">
          <img src={images[selectedIndex].url} alt={images[selectedIndex].alt} className="w-full h-full object-cover transition-opacity duration-300 block" />
          <div className="absolute top-2 left-2 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wide backdrop-blur-md">
            Best Seller
          </div>
        </div>

        <div className="w-12 h-full flex flex-col justify-between flex-shrink-0">
          <button onClick={() => handleScroll('up')} className="flex items-center justify-center w-full h-5 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-md transition-colors mb-1 flex-shrink-0 border border-gray-100 shadow-sm z-10" type="button">
            <ChevronUp size={12} strokeWidth={2.5} />
          </button>

          <div ref={mobileScrollRef} className="flex-1 overflow-y-auto flex flex-col gap-1.5 min-h-0 py-0.5" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {images.map((img, idx) => (
              <button key={img.id} onClick={() => setSelectedIndex(idx)} className={`w-full aspect-square rounded-md overflow-hidden border-2 flex-shrink-0 transition-all duration-200 ${selectedIndex === idx ? 'border-brand-600 shadow-md scale-95 ring-1 ring-brand-100' : 'border-transparent opacity-70 hover:opacity-100 bg-gray-50'}`} type="button">
                <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          <button onClick={() => handleScroll('down')} className="flex items-center justify-center w-full h-5 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-md transition-colors mt-1 flex-shrink-0 border border-gray-100 shadow-sm z-10" type="button">
            <ChevronDown size={12} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <div className="hidden sm:flex flex-col items-center w-full">
        <div className="w-[350px] h-[350px] bg-gray-50 rounded-xl overflow-hidden border border-gray-100 relative shadow-sm mb-3">
          <img src={images[selectedIndex].url} alt={images[selectedIndex].alt} className="w-full h-full object-cover transition-opacity duration-300 block" />
          <div className="absolute top-2 left-2 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wide backdrop-blur-md">
            Best Seller
          </div>
        </div>

        <div className="w-[350px] flex items-center gap-2">
          <button onClick={() => handleScroll('left')} className="flex items-center justify-center w-6 h-8 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-md transition-colors flex-shrink-0 border border-gray-100 shadow-sm" type="button">
            <ChevronLeft size={16} strokeWidth={2.5} />
          </button>

          <div ref={desktopScrollRef} className="flex-1 overflow-x-auto flex gap-2 py-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {images.map((img, idx) => (
              <button key={img.id} onClick={() => setSelectedIndex(idx)} className={`w-16 h-16 rounded-md overflow-hidden border-2 flex-shrink-0 transition-all duration-200 ${selectedIndex === idx ? 'border-brand-600 shadow-md scale-95 ring-1 ring-brand-100' : 'border-transparent opacity-70 hover:opacity-100 bg-gray-50'}`} type="button">
                <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          <button onClick={() => handleScroll('right')} className="flex items-center justify-center w-6 h-8 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-md transition-colors flex-shrink-0 border border-gray-100 shadow-sm" type="button">
            <ChevronRight size={16} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export const ProductSheetContent = () => {
  const [quantity, setQuantity] = useState(1);
  const [productData, setProductData] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const navigate = useNavigate();
  const { productDrawer, closeProductDrawer, initializeCart, closeProductDrawerForModal } = useAppStore();
  const { setCheckoutItems, setShowAddToCartModal } = useCheckoutStore();

  // FIX: This ref prevents the useEffect from running more than once for the same product ID
  const lastFetchedId = useRef(null);

  useEffect(() => {
    const fetchProductData = async () => {
      if (!productDrawer.product?.id) return;
      // Prevent duplicate call if this ID was just fetched
      if (lastFetchedId.current === productDrawer.product.id) return;
      
      setLoading(true);
      lastFetchedId.current = productDrawer.product.id;
      const tracking_data = getTrackingMetadata();

      try {
        const response = await axios.post(`${BASE_URL}/api/get_product_page_data/`, {
          product_id: productDrawer.product.id, tracking_data: tracking_data
        });
        const data = response.data;
        setProductData(data);

        pushToDataLayer('view_content', {
          event_id: tracking_data.event_id,
          value: data.discountPrice || data.price,
          currency: 'BDT',
          content_name: data.name,
          content_ids: [String(data.id)],
          content_type: 'product'
        }, { external_id: localStorage.getItem("customerId") });

        if (data.colors && data.colors.length > 0) {
          const defaultColor = data.colors[0];
          setSelectedVariant({ id: defaultColor.id, name: defaultColor.name, quantity: 1 });
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
        lastFetchedId.current = null; // Reset on error to allow retry
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [productDrawer.product?.id]);

  useEffect(() => {
    if (selectedVariant) {
      setSelectedVariant(prev => ({ ...prev, quantity: quantity }));
    }
  }, [quantity]);

  const increment = () => setQuantity(q => Math.min(10, q + 1));
  const decrement = () => setQuantity(q => Math.max(1, q - 1));

  const handleCheckout = () => {
    if (selectedVariant && productData) {
      const checkoutData = [{
        "id": '1', 
        "name": productData.name, 
        "variant_type": !productData.hasVariants ? "Color" : (productData.colors ? "Color" : "Size"), 
        "variant": selectedVariant.name, 
        "price": productData.discountPrice ? productData.discountPrice : productData.price,
        "originalPrice": productData.price, 
        "quantity": selectedVariant.quantity, 
        "image": productData.images.find(img => img.variantId === selectedVariant.id)?.selectorImage || productData.images[0].selectorImage,
        "productId": productData.id, 
        "variantId": selectedVariant.id
      }];
      setCheckoutItems(checkoutData);
      navigate("/checkout");
      closeProductDrawer();
    }
  };

  const handleAddToCart = async () => {
    setIsAddingToCart(true);
    const tracking_data = getManualTrackingMetadata();
    try {
      const customerId = localStorage.getItem('customerId');
      const payload = {
        customer_id: customerId,
        product_id: productData.id,
        variant_id: selectedVariant.id,
        quantity, 
        tracking_data: tracking_data
      };
      const { status } = await axios.post(`${BASE_URL}/api/add_to_cart/`, payload);
      if (status === 200) {
        closeProductDrawerForModal();
        setShowAddToCartModal(true);
        pushToDataLayer('add_to_cart', {
          event_id: tracking_data.event_id,
          value: ((productData.discountPrice || productData.price) * (quantity || 1)).toFixed(2),
          currency: 'BDT',
          content_name: productData.name,
          content_ids: [String(productData.id)],
          content_type: 'product',
          num_items: quantity || 1
        }, { external_id: customerId });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setIsAddingToCart(false);
      initializeCart();
    }
  };

  if (loading) {
    return (
      <div className="px-5 flex flex-col min-h-full sm:pt-4 items-center justify-center h-[400px]">
        <Spin size="large" />
        <p className="mt-4 text-gray-600">Loading product details...</p>
      </div>
    );
  }

  // Fallback data restored exactly as original
  const product = productData ? {
    id: productData.id,
    name: productData.name,
    price: productData.discountPrice || productData.price,
    originalPrice: productData.discountPrice ? productData.price : null,
    rating: productData.rating,
    reviewCount: productData.reviewCount,
    features: productData.features,
    images: productData.images?.map((img, index) => ({
      id: img.id || `img-${index}`,
      url: img.mainImage ? `${BASE_URL}${img.mainImage}` : 'https://picsum.photos/800/800?random=' + (index + 1),
      alt: img.alt || productData.name
    }))
  } : {
    id: 'p-123',
    name: 'Korean Premium Crossbody Bag ',
    price: 299.99,
    originalPrice: 349.99,
    rating: 4.8,
    reviewCount: 2150,
    features: ['Material: Premium PU Leater', 'Size: 19x17x6 cm', 'Origin: Imported from China'],
    images: [
      { id: '1', url: 'https://picsum.photos/800/800?random=1', alt: 'Headphones Front View' },
      { id: '2', url: 'https://picsum.photos/800/800?random=2', alt: 'Headphones Side View' },
      { id: '3', url: 'https://picsum.photos/800/800?random=3', alt: 'Headphones Case' },
      { id: '4', url: 'https://picsum.photos/800/800?random=4', alt: 'Headphones Lifestyle' }
    ]
  };

  return (
    <div className="px-5 flex flex-col min-h-full sm:pt-4">
      <div className="flex-1 pb-2 pt-2 overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <h2 className="text-lg font-bold text-gray-900 leading-tight mb-4 tracking-tight line-clamp-1">{product.name}</h2>
        <div className="mb-3">
          <ProductGallery images={product.images} />
        </div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-extrabold text-brand-600">৳ {product.price.toFixed(2)}</span>
            {product.originalPrice && (
              <span className="text-sm text-gray-400 line-through font-medium">৳ {product.originalPrice.toFixed(2)}</span>
            )}
          </div>
          <div className="flex items-center gap-1 bg-yellow-50 px-1.5 py-0.5 rounded-md border border-yellow-100">
            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
            <span className="font-bold text-gray-800 text-xs">{product.rating}</span>
            <span className="text-[10px] text-gray-500">({product.reviewCount})</span>
          </div>
        </div>
        <div className="mb-4">
          <h3 className="text-[10px] sm:text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5">Highlights</h3>
          <ul className="space-y-1.5">
            {product.features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-gray-600 leading-snug font-medium">
                <div className="w-1 h-1 rounded-full bg-gray-500 mt-1.5 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center text-green-600 flex-shrink-0"><Truck size={14} /></div>
            <div className="flex flex-col">
              <span className="text-[10px] sm:text-xs font-bold text-gray-900 uppercase">Fast Delivery</span>
              <span className="text-[10px] sm:text-xs text-gray-500 leading-none">2-3 business days</span>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0"><RotateCcw size={14} /></div>
            <div className="flex flex-col">
              <span className="text-[10px] sm:text-xs font-bold text-gray-900 uppercase">Easy Returns</span>
              <span className="text-[10px] sm:text-xs text-gray-500 leading-none">3 days policy</span>
            </div>
          </div>
        </div>
      </div>
      <div className="sticky bottom-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 py-3 -mx-5 px-5 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] z-10">
        <div className="flex items-center gap-3 h-10">
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5 h-full w-[100px] flex-shrink-0 border border-gray-200">
            <button onClick={decrement} disabled={quantity <= 1} className="w-8 h-full flex items-center justify-center text-gray-500 rounded-md transition-all disabled:opacity-30"><Minus size={14} strokeWidth={2.5} /></button>
            <span className="flex-1 text-center font-bold text-gray-900 text-sm">{quantity}</span>
            <button onClick={increment} className="w-8 h-full flex items-center justify-center text-gray-500 rounded-md transition-all"><Plus size={14} strokeWidth={2.5} /></button>
          </div>
          <button type="button" className="flex-1 h-full bg-gray-900 text-white rounded-lg flex items-center justify-center gap-2 shadow-lg transition-all" onClick={handleCheckout}>
            <span className="text-sm font-bold tracking-wide">Checkout</span>
          </button>
          <button type="button" onClick={handleAddToCart} className="w-[40px] h-full bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center gap-2 transition-all">
            {isAddingToCart ? (
              <svg className="animate-spin h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
            ) : <CartIcon className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </div>
  );
};